// v222 — Per-project Build-Info browse page (item 33 Phase A of
// docs/PRODUCT-ROADMAP.md).
//
// Tabular list of every build CI has published into this project,
// most-recent first. A `name` filter lives in the topbar so an
// operator who only cares about one CI lane can narrow quickly.

import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Activity, ExternalLink, GitBranch, Search } from 'lucide-react';
import { Card, CardHeader } from '../../components/ui/Card';
import { Input } from '../../components/ui/Input';
import { Badge } from '../../components/ui/Badge';
import { useProjects } from '../../api/client';
import { useProjectBuilds } from '../../api/buildInfo';

function shortRev(rev?: string): string {
  if (!rev) return '';
  return rev.length > 12 ? rev.slice(0, 12) : rev;
}

function buildsCountLabel(data: { builds: unknown[] } | undefined): string | undefined {
  if (!data) return undefined;
  return `${data.builds.length} build${data.builds.length === 1 ? '' : 's'} (most-recent 200)`;
}

export function ProjectBuilds() {
  const { id } = useParams<{ id: string }>();
  const { data: projects } = useProjects();
  const project = projects?.find((p) => p.id === id);

  const [nameFilter, setNameFilter] = useState('');
  const { data, error, isLoading } = useProjectBuilds(id, {
    name: nameFilter.trim() || undefined,
    limit: 200,
  });

  return (
    <div className="space-y-6">
      <div>
        <Link
          to="/builds"
          className="inline-flex items-center gap-2 text-sm text-fg-muted hover:text-fg mb-2"
        >
          <ArrowLeft className="h-4 w-4" />
          All projects
        </Link>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className="orb-eyebrow text-accent/80 mb-1">
              {project?.slug ?? id} · Provenance
            </p>
            <h1 className="text-2xl font-display font-semibold text-fg leading-tight">
              Build-Info
            </h1>
            <p className="text-fg-muted text-sm mt-2 max-w-3xl">
              Builds CI has declared against this project, most-recent first.
              Each row links to a detail page with the modules, artifacts,
              dependencies, and the original JFrog-format JSON the CI
              submitted.
            </p>
          </div>
          <Link
            to={`/projects/${id}/builds/dependency-search`}
            className="inline-flex items-center gap-2 rounded-md border border-border bg-bg-elevated px-3 py-2 text-sm text-fg hover:border-accent hover:text-accent transition-colors"
            title="Find every build that consumed a given coordinate or content hash"
          >
            <Search className="h-4 w-4" />
            Reverse dep lookup
          </Link>
        </div>
      </div>

      <Card>
        <CardHeader
          title={
            <span className="inline-flex items-center gap-2">
              <Activity className="h-4 w-4 text-accent" />
              Builds
            </span>
          }
          description={buildsCountLabel(data)}
          actions={
            <Input
              placeholder="filter by name…"
              value={nameFilter}
              onChange={(e) => setNameFilter(e.target.value)}
              className="w-64"
            />
          }
        />
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-fg-muted border-b border-border">
                <th className="px-4 py-2 font-medium">Name</th>
                <th className="px-4 py-2 font-medium">Number</th>
                <th className="px-4 py-2 font-medium">Started</th>
                <th className="px-4 py-2 font-medium">VCS</th>
                <th className="px-4 py-2 font-medium">Agent</th>
                <th className="px-4 py-2 font-medium">Artifacts</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr>
                  <td className="px-4 py-3 text-fg-muted" colSpan={6}>
                    Loading…
                  </td>
                </tr>
              )}
              {error && (
                <tr>
                  <td className="px-4 py-3 text-danger" colSpan={6}>
                    {String(error)}
                  </td>
                </tr>
              )}
              {data?.builds.length === 0 && !isLoading && (
                <tr>
                  <td className="px-4 py-3 text-fg-muted" colSpan={6}>
                    No builds yet — CI lanes publish against this project via{' '}
                    <code className="font-mono mx-1">
                      PUT /api/build?project={project?.slug ?? '…'}
                    </code>{' '}
                    with a JFrog-format Build-Info body.
                  </td>
                </tr>
              )}
              {data?.builds.map((b) => (
                <tr key={b.id} className="border-b border-border/50 hover:bg-bg-hover/40">
                  <td className="px-4 py-2 font-medium text-fg">
                    <Link
                      to={`/projects/${id}/builds/${encodeURIComponent(b.name)}/${encodeURIComponent(b.number)}`}
                      className="hover:text-accent"
                    >
                      {b.name}
                    </Link>
                  </td>
                  <td className="px-4 py-2 font-mono text-fg-muted">
                    <Link
                      to={`/projects/${id}/builds/${encodeURIComponent(b.name)}/${encodeURIComponent(b.number)}`}
                      className="hover:text-accent"
                    >
                      #{b.number}
                    </Link>
                  </td>
                  <td className="px-4 py-2 text-fg-muted">
                    {new Date(b.started).toLocaleString()}
                  </td>
                  <td className="px-4 py-2 text-fg-muted">
                    {b.vcs_revision ? (
                      <span className="inline-flex items-center gap-1.5">
                        <GitBranch className="h-3.5 w-3.5 shrink-0" />
                        <code className="font-mono text-xs">
                          {shortRev(b.vcs_revision)}
                        </code>
                        {b.vcs_url && (
                          <a
                            href={b.vcs_url}
                            target="_blank"
                            rel="noreferrer"
                            className="text-accent hover:underline"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <ExternalLink className="h-3 w-3" />
                          </a>
                        )}
                      </span>
                    ) : (
                      <span className="text-fg-subtle">—</span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-fg-muted text-xs">
                    {b.agent_name ? (
                      <span>
                        {b.agent_name}
                        {b.agent_version && (
                          <span className="text-fg-subtle"> · {b.agent_version}</span>
                        )}
                      </span>
                    ) : (
                      <span className="text-fg-subtle">—</span>
                    )}
                  </td>
                  <td className="px-4 py-2 tabular-nums">
                    <Badge tone={b.artifacts_count > 0 ? 'accent' : 'neutral'}>
                      {b.artifacts_count}
                    </Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
